import { RefreshCw } from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

export interface HourlyDataPoint {
  hour: number
  yesterdayTotal: number
  yesterday: number
  today: number
}

interface HourlyTotals {
  yesterdayTotal: number
  yesterday: number
  today: number
}

interface HourlyTrendChartProps {
  title: string
  data: HourlyDataPoint[]
  totals: HourlyTotals
  currentHour: number
  loading?: boolean
  isCurrency?: boolean
  isPercentage?: boolean
  onRefresh?: () => void
}

const formatValue = (value: number, isCurrency?: boolean, isPercentage?: boolean) => {
  if (isCurrency) {
    if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}K`
    return `$${value.toFixed(0)}`
  }
  if (isPercentage) {
    return `${value.toFixed(2)}%`
  }
  return value.toFixed(2)
}

const formatLegendValue = (value: number, isCurrency?: boolean, isPercentage?: boolean) => {
  if (isCurrency) {
    if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(2)}M`
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
    return `$${value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
  }
  if (isPercentage) {
    return `${value.toFixed(2)}%`
  }
  return value.toFixed(2)
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    dataKey: string
    value: number
    color: string
    name: string
  }>
  label?: number
  isCurrency?: boolean
  isPercentage?: boolean
}

const CustomTooltip = ({ active, payload, label, isCurrency, isPercentage }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null

  return (
    <div className="bg-tf-bg-card border border-tf-border rounded-lg shadow-lg p-3 min-w-[120px]">
      <p className="text-sm font-bold text-tf-text mb-2 text-center border-b pb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 py-1">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm text-tf-text-muted">{entry.name}:</span>
          <span className="text-sm font-semibold ml-auto" style={{ color: entry.color }}>
            {formatValue(entry.value, isCurrency, isPercentage)}
          </span>
        </div>
      ))}
    </div>
  )
}

export function HourlyTrendChart({
  title,
  data,
  totals,
  currentHour,
  loading,
  isCurrency,
  isPercentage,
  onRefresh,
}: HourlyTrendChartProps) {
  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-tf-text">{title}</h3>
        </div>
        <div className="h-48 bg-tf-bg-muted rounded animate-pulse" />
      </div>
    )
  }

  // Filter data to only show up to current hour for today/yesterday
  const chartData = data.map((d) => ({
    hour: d.hour,
    'Yesterday total': d.yesterdayTotal,
    'Yesterday': d.hour <= currentHour ? d.yesterday : null,
    'Today': d.hour <= currentHour ? d.today : null,
  }))

  // Calculate Y-axis domain
  const allValues = data.flatMap(d => [d.yesterdayTotal, d.yesterday, d.today]).filter(v => v !== null && v !== undefined)
  const maxValue = Math.max(...allValues) * 1.1 || 1
  const minValue = Math.min(...allValues) * 0.9

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-tf-text">{title}</h3>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="p-1 rounded hover:bg-tf-bg-hover transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5 text-tf-text-muted" />
          </button>
        )}
      </div>

      {/* Chart */}
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis
              dataKey="hour"
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={false}
              interval={2}
            />
            <YAxis
              tickFormatter={(v) => formatValue(v, isCurrency, isPercentage)}
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              domain={[minValue, maxValue]}
              width={55}
            />
            <Tooltip content={<CustomTooltip isCurrency={isCurrency} isPercentage={isPercentage} />} />
            <Line
              type="monotone"
              dataKey="Yesterday total"
              stroke="#d1d5db"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 4 }}
              strokeDasharray="5 5"
            />
            <Line
              type="monotone"
              dataKey="Yesterday"
              stroke="#9ca3af"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="Today"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend with totals */}
      <div className="mt-3 pt-3 border-t border-tf-border-sub space-y-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-tf-border" style={{ backgroundImage: 'linear-gradient(90deg, #d1d5db 50%, transparent 50%)', backgroundSize: '6px 1px' }} />
            <span className="text-tf-text-muted">Yesterday total</span>
          </div>
          <span className="font-semibold text-tf-text-sub">
            {formatLegendValue(totals.yesterdayTotal, isCurrency, isPercentage)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-tf-text-muted" />
            <span className="text-tf-text-muted">Yesterday</span>
          </div>
          <span className="font-semibold text-tf-text-sub">
            {formatLegendValue(totals.yesterday, isCurrency, isPercentage)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-tf-feedback-info-bg" />
            <span className="text-tf-text-muted">Today</span>
          </div>
          <span className="font-semibold text-tf-feedback-info-text">
            {formatLegendValue(totals.today, isCurrency, isPercentage)}
          </span>
        </div>
      </div>
    </div>
  )
}

export default HourlyTrendChart
